import Vue from 'vue'
import axios from '@/axios-auth'

const state = {
  certificateOfDeath: null,
  certificateOfInheritance: null,
  contactPersons: [],
  contactPerson: {},
  invitations: [],
  invitation: {},
  policyHolders: [],
  policyHolder: {},
  policyHolderContracts: [],
  relationTypes: [
    'SPOUSE',
    'COMPANION',
    'FRIEND',
    'EXTENDED_FAMILY',
    'CHILD',
    'SIBLING'
  ]
}

const getters = {
  certificateOfDeath: state => state.certificateOfDeath,
  certificateOfInheritance: state => state.certificateOfInheritance,
  contactPersons: state => state.contactPersons,
  contactPerson: state => state.contactPerson,
  invitations: state => state.invitations,
  invitation: state => state.invitation,
  policyHolders: state => state.policyHolders,
  policyHolder: state => state.policyHolder,
  policyHolderContracts: state => state.policyHolderContracts,
  relationTypes: state => state.relationTypes
}

const actions = {
  createContactPersonRelation ({dispatch}, contactPersonRelation) {
    return new Promise((resolve, reject) => {
      const authValue = 'Bearer '.concat(contactPersonRelation.idToken)
      axios.post('/users/createContactPersonRelation', contactPersonRelation, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  deleteInvitation ({dispatch}, uuid) {
    return new Promise((resolve, reject) => {
      axios.delete(`/invitations/${uuid}`)
        .then(() => {
          dispatch('setLogoutTimer')
          resolve()
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
  deleteContactPerson ({getters, commit, dispatch}, id) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.delete(`/users/${currentUser.id}/contactPersons/${id}`, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        commit('deleteContactPerson', id)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchContactPersons ({getters, commit, dispatch}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get(`/users/${currentUser.id}/contactPersons`, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setContactPersons', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchContactPersonById ({getters, commit, dispatch}, id) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get(`/users/${currentUser.id}/contactPersons/${id}`, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setContactPerson', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchContractsByPolicyHolderId ({getters, commit, dispatch}, policyHolderId) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get(`/contracts`, {
        params: {
          policyHolderId: policyHolderId
        },
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setPolicyHolderContracts', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => {
        switch (err.response.status) {
          case 403: { // certificate of death or certificate of inheritance not approved yet
            commit('setPolicyHolderContracts', null)
            dispatch('setLogoutTimer')
            resolve()
            break
          }
          default: {
            reject(err)
          }
        }
      })
    })
  },
  fetchCertificateOfDeath ({getters, commit, dispatch}, policyHolderId) {
    const currentUser = getters.currentUser
    const idToken = currentUser.idToken
    const authValue = 'Bearer '.concat(idToken)
    return new Promise((resolve, reject) => {
      axios.get(`/users/${policyHolderId}/certificateOfDeath/meta`, {
        headers: {
          Authorization: authValue,
          'Content-Type': 'application/json'
        }
      }).then((metaResponse) => {
        axios.get(`/users/${policyHolderId}/certificateOfDeath`, {
          responseType: 'arraybuffer',
          headers: {
            Authorization: authValue,
            'Content-Type': 'application/json'
          }
        }).then((response) => {
          metaResponse.data.file = response.data
          commit('setCertificateOfDeath', metaResponse.data)
          dispatch('setLogoutTimer')
          resolve()
        }).catch(() => {
          commit('setCertificateOfDeath', null)
        })
      }).catch((err) => {
        switch (err.response.status) {
          case 404: { // no certificate of death uploaded yet
            dispatch('setLogoutTimer')
            resolve()
            break
          }
          default: {
            reject(err)
          }
        }
      })
    })
  },
  fetchCertificateOfInheritance ({getters, commit, dispatch}, policyHolderId) {
    const currentUser = getters.currentUser
    const idToken = currentUser.idToken
    const authValue = 'Bearer '.concat(idToken)
    return new Promise((resolve, reject) => {
      axios.get(`/users/${policyHolderId}/contactPersons/${currentUser.id}/certificateOfInheritance`, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        if (response.data.certificateOfInheritance) {
          const certificateOfInheritanceMeta = {}
          certificateOfInheritanceMeta.file = response.data.certificateOfInheritance
          commit('setCertificateOfInheritance', certificateOfInheritanceMeta)
        } else {
          commit('setCertificateOfInheritance', null)
        }
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => {
        commit('setCertificateOfInheritance', null)
        reject(err)
      })
    })
  },
  fetchDocuments ({getters, commit, dispatch}, policyHolderId) {
    return dispatch('fetchCertificateOfDeath', policyHolderId).then(() => {
      dispatch('fetchCertificateOfInheritance', policyHolderId)
    })
  },
  fetchInvitations ({getters, commit, dispatch}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get('/invitations', {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setInvitations', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchInvitationByUuid ({commit}, uuid) {
    return new Promise((resolve, reject) => {
      axios.get(`/invitations/${uuid}`)
        .then((response) => {
          commit('setInvitation', response.data)
          resolve()
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
  fetchPolicyHolders ({getters, commit, dispatch}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get(`/users/${currentUser.id}/policyHolders`, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setPolicyHolders', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchPolicyHolderById ({getters, commit, dispatch}, id) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get(`/users/${currentUser.id}/policyHolders/${id}`, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('setPolicyHolder', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  inviteContactPerson ({getters, commit, dispatch}, contactPerson) {
    return new Promise((resolve, reject) => {
      const invitation = {
        contactPersonEmail: contactPerson.email,
        contactPersonFirstname: contactPerson.firstName,
        contactPersonLastname: contactPerson.lastName,
        contactPersonSalutation: contactPerson.salutation
      }
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.post(`/invitations`, invitation, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('addInvitation', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => {
        reject(err)
      })
    })
  },
  deleteCertificateOfDeath ({getters, commit, dispatch}, policyHolderId) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.delete(`/users/${policyHolderId}/certificateOfDeath`, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        commit('setCertificateOfDeath', null)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  uploadCertificateOfDeath ({getters, commit, dispatch}, data) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      const formData = new FormData()
      formData.append('file', data.certificateOfDeath)
      axios.post(`/users/${data.policyHolderId}/certificateOfDeath`, formData, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        const certificateOfDeathMeta = {}
        certificateOfDeathMeta.uploaderId = currentUser.id
        certificateOfDeathMeta.filename = data.certificateOfDeath.name
        certificateOfDeathMeta.file = data.certificateOfDeath
        commit('setCertificateOfDeath', certificateOfDeathMeta)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  deleteCertificateOfInheritance ({getters, commit, dispatch}, policyHolderId) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.delete(`/users/${policyHolderId}/contactPersons/${currentUser.id}/certificateOfInheritance`, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        commit('setCertificateOfInheritance', null)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  uploadCertificateOfInheritance ({getters, commit, dispatch}, data) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.put(`/users/${data.policyHolderId}/contactPersons/${currentUser.id}/certificateOfInheritance`, data.documents, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        const certificateOfInheritanceMeta = {}
        certificateOfInheritanceMeta.file = response.data.certificateOfInheritance
        certificateOfInheritanceMeta.filename = response.data.certificateOfInheritance.name
        commit('setCertificateOfInheritance', certificateOfInheritanceMeta)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  }
}
const mutations = {
  addContactPerson (state, contactPerson) {
    state.contactPersons.push(contactPerson)
  },
  addInvitation (state, invitation) {
    state.invitations.push(invitation)
  },
  deleteContactPerson (state, id) {
    const indexToDelete = state.contactPersons.findIndex((contactPerson) => contactPerson.id === id)
    Vue.delete(state.contactPersons, indexToDelete)
  },
  setContactPersons (state, contactPersons) {
    state.contactPersons = contactPersons
  },
  setContactPerson (state, contactPerson) {
    state.contactPerson = contactPerson
  },
  setCertificateOfDeath (state, certificateOfDeath) {
    state.certificateOfDeath = certificateOfDeath
  },
  setCertificateOfInheritance (state, certificateOfInheritance) {
    state.certificateOfInheritance = certificateOfInheritance
  },
  setInvitations (state, invitations) {
    state.invitations = invitations
  },
  setInvitation (state, invitation) {
    state.invitation = invitation
  },
  setPolicyHolders (state, policyHolders) {
    state.policyHolders = policyHolders
  },
  setPolicyHolder (state, policyHolder) {
    state.policyHolder = policyHolder
  },
  setPolicyHolderContracts (state, policyHolderContracts) {
    state.policyHolderContracts = policyHolderContracts
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/contactPersons.js